import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Actions, Errors, trackAction, trackError } from "modules/monitoring";
import { DeviceInfoState, PermissionState, SerializedDevice } from "../types";
import {
  askDevicesPermissions,
  detectMobile,
  enumerateDevices,
} from "./actions";
import {
  checkHasAudioInputPermissions,
  checkHasVideoInputPermissions,
  deserializeError,
} from "./utils";

export const deviceInfoInitialState: DeviceInfoState = {
  devices: [],

  permissions: {
    audio: PermissionState.NOT_GRANTED,
    video: PermissionState.NOT_GRANTED,
  },

  activeDevices: {
    audioInput: null,
    audioOutput: null,
    videoInput: null,
  },

  preEnable: {
    audioInput: true,
    videoInput: false,
  },

  noCamOrMic: false,
  isMobile: false,
};

export const deviceInfo = createSlice({
  name: "devices",
  initialState: deviceInfoInitialState,
  reducers: {
    setDevicePermissions: (
      state,
      action: PayloadAction<{
        audio: PermissionState;
        video: PermissionState;
      }>,
    ) => {
      state.permissions.audio = action.payload.audio;
      state.permissions.video = action.payload.video;
    },

    setNoCamOrMic: (state, action: PayloadAction<boolean>) => {
      state.noCamOrMic = action.payload;
    },

    setActiveAudioInput: (
      state,
      action: PayloadAction<SerializedDevice | MediaDeviceInfo>,
    ) => {
      state.activeDevices.audioInput = action.payload;
    },
    setActiveAudioOutput: (
      state,
      action: PayloadAction<SerializedDevice | MediaDeviceInfo>,
    ) => {
      state.activeDevices.audioOutput = action.payload;
    },
    setActiveVideoInput: (
      state,
      action: PayloadAction<SerializedDevice | MediaDeviceInfo>,
    ) => {
      state.activeDevices.videoInput = action.payload;
    },

    setIsAudioInputPreEnabled: (state, action: PayloadAction<boolean>) => {
      state.preEnable.audioInput = action.payload;
    },

    setIsVideoInputPreEnabled: (state, action: PayloadAction<boolean>) => {
      state.preEnable.videoInput = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(
        askDevicesPermissions.fulfilled,
        (state, { meta: { arg: availableMediaStreamConstraints } }) => {
          if (availableMediaStreamConstraints.audio) {
            state.permissions.audio = PermissionState.GRANTED;
          }

          if (availableMediaStreamConstraints.video) {
            state.permissions.video = PermissionState.GRANTED;
          }

          trackAction(Actions.GET_USER_MEDIA_SUCCESS, {
            constraints: availableMediaStreamConstraints,
          });
        },
      )

      .addCase(askDevicesPermissions.rejected, (state, action) => {
        const { error } = action;

        trackError(deserializeError(error), {
          label: Errors.GET_USER_MEDIA,
        });
      })

      .addCase(enumerateDevices.rejected, (state, action) => {
        const { error } = action;

        trackError(deserializeError(error), {
          label: Errors.MEDIA_DEVICE_ENUM,
        });
      })
      .addCase(enumerateDevices.fulfilled, (state, action) => {
        state.devices = action.payload;

        const hasAudioInputPermissions = checkHasAudioInputPermissions(
          action.payload,
        );
        const hasVideoInputPermissions = checkHasVideoInputPermissions(
          action.payload,
        );

        if (hasAudioInputPermissions) {
          state.permissions.audio = PermissionState.GRANTED;
        }

        if (hasVideoInputPermissions) {
          state.permissions.video = PermissionState.GRANTED;
        }

        trackAction(
          Actions.MEDIA_DEVICE_ENUM_SUCCESS,
          JSON.parse(JSON.stringify(state)),
        );
      })
      .addCase(detectMobile.fulfilled, (state, action) => {
        state.isMobile = action.payload;
      }),
});

export const {
  setDevicePermissions,
  setNoCamOrMic,
  setActiveAudioInput,
  setActiveAudioOutput,
  setActiveVideoInput,
  setIsAudioInputPreEnabled,
  setIsVideoInputPreEnabled,
} = deviceInfo.actions;

export default deviceInfo.reducer;
